/**
 * ChartView — chart spec controls (type / X / Y / series) wrapping Chart.jsx.
 *
 * Self-contained: derives smart defaults from the data, recomputes the render
 * model on any change (web/src/lib/chart.js), and exports the live SVG to PNG
 * via the SVG→canvas trick (same as ERDiagramPanel — no dependency). Mounted on
 * a query result set, and reused later by the report builder.
 *
 * Props: { columns, rows, title? }  (columns: [{name,...}] | [string], rows: [[..]])
 */
import { createMemo, createEffect, For, Show, batch } from 'solid-js';
import { createStore } from 'solid-js/store';
import { CHART_TYPES, buildChartModel, defaultSpec, inferColumnKinds } from '../lib/chart.js';
import Chart from './Chart.jsx';
import { toast } from '../lib/toast.js';

export default function ChartView(props) {
  const columns = () => props.columns || [];
  const rows = () => props.rows || [];
  const colNames = createMemo(() => columns().map((c) => (typeof c === 'string' ? c : c.name)));
  const kinds = createMemo(() => inferColumnKinds(columns(), rows()));
  const numericNames = createMemo(() => kinds().filter((k) => k.numeric).map((k) => k.name));

  const [spec, setSpec] = createStore(
    props.initialSpec ? { type: 'bar', x: '', y: [], series: '', ...props.initialSpec } : { type: 'bar', x: '', y: [], series: '' });

  // (Re)seed whenever the underlying columns change to something the current
  // spec doesn't fit (new result set, or first mount). A provided initialSpec
  // (report editor) wins when its columns are present; else smart defaults.
  createEffect(() => {
    const names = colNames();
    if (names.length === 0) return;
    const xOk = spec.x && names.includes(spec.x);
    const yOk = spec.y.length && spec.y.every((y) => names.includes(y));
    if (!xOk || !yOk) {
      const init = props.initialSpec;
      const fits = init && init.x && names.includes(init.x) && (init.y || []).every((y) => names.includes(y));
      const d = fits ? init : defaultSpec(columns(), rows());
      batch(() => { setSpec('type', d.type || 'bar'); setSpec('x', d.x); setSpec('y', d.y || []); setSpec('series', d.series || ''); });
    }
  });

  // Notify the parent (report editor) of the chosen spec so it can be persisted.
  createEffect(() => {
    const snap = { type: spec.type, x: spec.x, y: [...spec.y], series: spec.series };
    props.onSpecChange?.(snap);
  });

  const model = createMemo(() => buildChartModel(columns(), rows(), spec));

  const toggleY = (name) => {
    setSpec('y', (ys) => (ys.includes(name) ? ys.filter((y) => y !== name) : [...ys, name]));
  };

  let svgRef;
  const exportPNG = () => {
    if (!svgRef) return;
    const clone = svgRef.cloneNode(true);
    clone.setAttribute('width', '960');
    clone.setAttribute('height', '520');
    // Standalone SVG has no app stylesheet — inline the class styles + a white bg.
    const style = document.createElementNS('http://www.w3.org/2000/svg', 'style');
    style.textContent = `
      text{font-family:ui-sans-serif,system-ui,sans-serif}
      .chart-grid line{stroke:#e5e7eb;stroke-width:1}
      .chart-axis{stroke:#94a3b8;stroke-width:1.2}
      .chart-tick{fill:#475569;font-size:12px}
      .chart-legend{fill:#0f172a;font-size:12px}
      .chart-line{stroke-width:2}
      .chart-area{opacity:0.18}
      .chart-point{stroke:#fff;stroke-width:1}
      .chart-bar{}
      .chart-slice{stroke:#fff;stroke-width:1}
      .chart-slice-lbl{fill:#fff;font-size:11px;font-weight:700}`;
    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bg.setAttribute('width', '960'); bg.setAttribute('height', '520'); bg.setAttribute('fill', '#ffffff');
    clone.insertBefore(bg, clone.firstChild);
    clone.insertBefore(style, clone.firstChild);

    const svgStr = new XMLSerializer().serializeToString(clone);
    const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const scale = 2;
      const cv = document.createElement('canvas');
      cv.width = 960 * scale; cv.height = 520 * scale;
      const ctx = cv.getContext('2d');
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      cv.toBlob((b) => {
        if (!b) { toast.error('PNG export failed'); return; }
        const a = document.createElement('a');
        a.href = URL.createObjectURL(b);
        a.download = `${(props.title || 'chart').replace(/[^\w.-]+/g, '_')}.png`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      }, 'image/png');
    };
    img.onerror = () => { URL.revokeObjectURL(url); toast.error('PNG export failed'); };
    img.src = url;
  };

  return (
    <div class="chart-view">
      <div class="chart-controls">
        <div class="chart-types">
          <For each={CHART_TYPES}>
            {(t) => (
              <button class="chart-typebtn" classList={{ active: spec.type === t.v }}
                      onClick={() => setSpec('type', t.v)}>{t.label}</button>
            )}
          </For>
        </div>
        <span class="chart-ctl">
          <label class="chart-lbl">{spec.type === 'pie' ? 'Labels' : 'X'}</label>
          <select class="input chart-sel" value={spec.x} onChange={(e) => setSpec('x', e.currentTarget.value)}>
            <option value="">—</option>
            <For each={colNames()}>{(n) => <option value={n}>{n}</option>}</For>
          </select>
        </span>
        <span class="chart-ctl chart-ctl-y">
          <label class="chart-lbl">{spec.type === 'pie' ? 'Value' : 'Y'}</label>
          <Show when={spec.type === 'pie'} fallback={
            <div class="chart-ymulti">
              <For each={colNames()} fallback={<span class="muted">no columns</span>}>
                {(n) => (
                  <label class="chart-ychip" classList={{ on: spec.y.includes(n), num: numericNames().includes(n) }}>
                    <input type="checkbox" checked={spec.y.includes(n)} onChange={() => toggleY(n)} />{n}
                  </label>
                )}
              </For>
            </div>
          }>
            <select class="input chart-sel" value={spec.y[0] || ''} onChange={(e) => setSpec('y', e.currentTarget.value ? [e.currentTarget.value] : [])}>
              <option value="">—</option>
              <For each={colNames()}>{(n) => <option value={n}>{n}</option>}</For>
            </select>
          </Show>
        </span>
        <Show when={spec.type !== 'pie'}>
          <span class="chart-ctl">
            <label class="chart-lbl" title="Pivot the first Y measure into one series per distinct value of this column">Series</label>
            <select class="input chart-sel" value={spec.series} onChange={(e) => setSpec('series', e.currentTarget.value)}>
              <option value="">(none)</option>
              <For each={colNames()}>{(n) => <option value={n}>{n}</option>}</For>
            </select>
          </span>
        </Show>
        <span class="spacer" />
        <button class="btn btn-sm btn-ghost" onClick={exportPNG} title="Export this chart as a PNG image">⤓ PNG</button>
      </div>

      <Show when={model().warnings.length > 0}>
        <div class="chart-warn">{model().warnings.join(' ')}</div>
      </Show>
      <Show when={spec.series && spec.y.length > 1 && spec.type !== 'pie'}>
        <div class="chart-note muted">Series set — only the first Y measure ({spec.y[0]}) is pivoted.</div>
      </Show>

      <div class="chart-canvas">
        <Show when={model().categories.length > 0 && model().series.length > 0}
              fallback={<div class="chart-empty muted">Pick an X and Y column to draw a chart.</div>}>
          <Chart model={model()} ref={(el) => (svgRef = el)} />
        </Show>
      </div>
    </div>
  );
}
